/**
 * Generic JSON sources (task 211; the ingest is task 129, Story 30).
 *
 * A generic source is anything that can POST JSON to
 * `POST /ingest/{source_id}` with its own key: a forwarder, a script, a
 * vendor webhook. Until this page an Admin could only create, key, map or
 * remove one by calling the API directly. It lists every visible Client's
 * sources, the typed `latticeai_dlp` source included (its mapping is built
 * in, task 180), and where the caller holds `manage_client` on a Client, the
 * moves for it.
 *
 * **A key is shown once.** Create and Rotate return the plaintext key and
 * nothing else does; the key-once modal holds it until it is closed, and no
 * row ever shows one.
 *
 * A Client whose services do not cover generic sources answers Create with
 * 409 `service_required` (task 179a). A platform Admin is offered to add the
 * service and retry; anyone else is told who can.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  ApiError,
  apiBaseUrl,
  createGenericSource,
  listGenericSources,
  listTenants,
  putGenericMapping,
  reissueGenericKey,
  removeGenericSource,
  type ClientTenant,
  type GenericSource,
  type GenericSourceResult,
  type GenericSourceWithKey,
} from '@bluesphere/api-client';
import { PillButton } from '@bluesphere/ui-kit';

import { useAccessToken } from '../auth/useAccessToken';
import { useTenant } from '../auth/useTenant';
import {
  absoluteTime,
  ErrorNote,
  INPUT,
  ModalShell,
  sourceLabel,
  timeAgo,
} from '@bluesphere/mdr-alert-panel';
import { INTEGRATION_PAGES } from '../lib/integrations';
import { ConfirmModal } from './ConfirmModal';
import { PageNav } from './PageNav';
import { EmptyState, ErrorState, Loading, SourceBadge } from './states';

const TYPE_GENERIC = 'generic';
const TEMPLATE = 'generic-json';

interface Target {
  tenant: ClientTenant;
  source: GenericSource;
}

export function GenericSources() {
  const idToken = useAccessToken();
  const { can } = useTenant();
  const queryClient = useQueryClient();
  const page = INTEGRATION_PAGES.generic;

  const tenants = useQuery({
    queryKey: ['tenants'],
    queryFn: async () => (await listTenants(idToken)).tenants,
    enabled: Boolean(idToken),
    refetchInterval: 15_000,
  });
  const rows = (tenants.data ?? [])
    .filter((t) => t.status !== 'revoked')
    .sort((a, b) => a.display_name.localeCompare(b.display_name));
  const sources = useQueries({
    queries: rows.map((t) => ({
      queryKey: ['generic-sources', t.client_tenant_id],
      queryFn: () => listGenericSources(t.client_tenant_id, idToken),
      enabled: Boolean(idToken),
      refetchInterval: 30_000,
    })),
  });
  const refresh = (tenantId: string) => {
    void queryClient.invalidateQueries({ queryKey: ['generic-sources', tenantId] });
    void queryClient.invalidateQueries({ queryKey: ['tenants'] });
  };

  const [createFor, setCreateFor] = useState<ClientTenant | null>(null);
  const [shownKey, setShownKey] = useState<{ tenant: ClientTenant; result: GenericSourceWithKey } | null>(
    null,
  );
  const [rotate, setRotate] = useState<Target | null>(null);
  const [remove, setRemove] = useState<Target | null>(null);
  const [mappingFor, setMappingFor] = useState<Target | null>(null);

  const rotateMutation = useMutation<GenericSourceWithKey, Error, Target>({
    mutationFn: ({ tenant, source }) =>
      reissueGenericKey(tenant.client_tenant_id, source.source_id, idToken),
    onSuccess: (result, { tenant }) => {
      setRotate(null);
      setShownKey({ tenant, result });
      refresh(tenant.client_tenant_id);
    },
  });
  const removeMutation = useMutation<GenericSourceResult, Error, Target>({
    mutationFn: ({ tenant, source }) =>
      removeGenericSource(tenant.client_tenant_id, source.source_id, idToken),
    onSuccess: (_result, { tenant }) => {
      setRemove(null);
      refresh(tenant.client_tenant_id);
    },
  });

  const connected = rows.filter((_t, i) =>
    (sources[i]?.data?.sources ?? []).some(
      (s) => s.state === 'enabled' && s.source_type === TYPE_GENERIC,
    ),
  ).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <PageNav backTo="/integrations" backLabel="Integrations" />
          <h1 className="mt-3 text-2xl font-semibold">{sourceLabel('generic')}</h1>
          <p className="mt-1 max-w-3xl text-sm text-white/60">{page.description}</p>
        </div>
        <div className="rounded-full bg-white/10 px-4 py-2 text-sm text-white">
          {connected} of {rows.length} Client{rows.length === 1 ? '' : 's'} connected
        </div>
      </div>

      <HowToSend />

      <div className="bs-card">
        <div className="flex flex-wrap items-baseline justify-between gap-2 px-5 pt-5">
          <h2 className="text-xl font-bold lg:text-2xl">Clients</h2>
          <span className="text-xs text-bluesphere-muted">{page.credentialNote}</span>
        </div>
        {tenants.isPending ? (
          <Loading height="h-40" />
        ) : tenants.isError ? (
          <div className="p-5">
            <ErrorState
              title="Could not load Clients"
              error={tenants.error}
              onRetry={() => void tenants.refetch()}
            />
          </div>
        ) : rows.length === 0 ? (
          <div className="p-5">
            <EmptyState
              title="No Clients yet"
              body="Add a Client on the Organizations page; its sources appear here."
            />
          </div>
        ) : (
          <div className="divide-y divide-bluesphere-border">
            {rows.map((tenant, i) => (
              <ClientSources
                key={tenant.client_tenant_id}
                tenant={tenant}
                query={sources[i]}
                canManage={can('manage_client', tenant.client_tenant_id)}
                onCreate={() => setCreateFor(tenant)}
                onRotate={(source) => setRotate({ tenant, source })}
                onRemove={(source) => setRemove({ tenant, source })}
                onMapping={(source) => setMappingFor({ tenant, source })}
              />
            ))}
          </div>
        )}
      </div>

      {createFor && (
        <CreateSourceModal
          tenant={createFor}
          canAddService={can('manage_platform')}
          onCancel={() => setCreateFor(null)}
          onCreated={(result) => {
            const tenant = createFor;
            setCreateFor(null);
            setShownKey({ tenant, result });
            refresh(tenant.client_tenant_id);
          }}
        />
      )}
      {shownKey && (
        <KeyOnceModal
          tenant={shownKey.tenant}
          result={shownKey.result}
          onClose={() => setShownKey(null)}
        />
      )}
      {rotate && (
        <ConfirmModal
          title={`Rotate the key for ${rotate.source.name}?`}
          confirmLabel="Rotate key"
          pendingLabel="Rotating…"
          onCancel={() => {
            setRotate(null);
            rotateMutation.reset();
          }}
          onConfirm={() => rotateMutation.mutate(rotate)}
          isPending={rotateMutation.isPending}
          error={rotateMutation.error}
        >
          <p className="text-sm text-bluesphere-textSecondary">
            A new key is issued and shown once. The current key stops working immediately, so
            whatever posts to this source ({rotate.tenant.display_name}) fails with 403 until it has
            the new one.
            {rotate.source.source_type !== TYPE_GENERIC &&
              ' This is the LatticeAI DLP forwarder’s key: update its stored key at the same time.'}
          </p>
        </ConfirmModal>
      )}
      {remove && (
        <ConfirmModal
          title={`Remove ${remove.source.name}?`}
          confirmLabel="Remove source"
          pendingLabel="Removing…"
          onCancel={() => {
            setRemove(null);
            removeMutation.reset();
          }}
          onConfirm={() => removeMutation.mutate(remove)}
          isPending={removeMutation.isPending}
          error={removeMutation.error}
        >
          <p className="text-sm text-bluesphere-textSecondary">
            The source <span className="font-mono">{remove.source.source_id}</span> on{' '}
            {remove.tenant.display_name} is removed and its key stops working. Events it already sent
            stay in the event store.
          </p>
        </ConfirmModal>
      )}
      {mappingFor && (
        <MappingModal
          target={mappingFor}
          canEdit={can('manage_client', mappingFor.tenant.client_tenant_id)}
          onClose={() => setMappingFor(null)}
          onSaved={() => {
            const id = mappingFor.tenant.client_tenant_id;
            setMappingFor(null);
            refresh(id);
          }}
        />
      )}
    </div>
  );
}

function HowToSend() {
  const base = apiBaseUrl() || 'https://<api host>';
  return (
    <div className="bs-card p-5">
      <h2 className="text-xl font-bold lg:text-2xl">How to send</h2>
      <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
        <HowField label="Endpoint">
          <span className="break-all font-mono text-xs">POST {base}/ingest/&lt;source id&gt;</span>
        </HowField>
        <HowField label="Header">
          <span className="font-mono text-xs">Authorization: Bearer &lt;the source&rsquo;s key&gt;</span>
        </HowField>
        <HowField label="Body">
          A JSON array, one JSON object, or NDJSON. At most 1 MB as sent; gzip accepted
          (<span className="font-mono text-xs">Content-Encoding: gzip</span>).
        </HowField>
        <HowField label="Responses">
          202 accepted. 400 or 413: fix the batch, never resend it. 403: the key no longer works. 429
          or 5xx: retry later. A retried batch is stored once.
        </HowField>
      </dl>
    </div>
  );
}

function HowField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs uppercase tracking-wider text-bluesphere-muted">{label}</dt>
      <dd className="mt-0.5 text-bluesphere-textOnLight">{children}</dd>
    </div>
  );
}

function ClientSources({
  tenant,
  query,
  canManage,
  onCreate,
  onRotate,
  onRemove,
  onMapping,
}: {
  tenant: ClientTenant;
  query: { data?: { sources: GenericSource[] }; isPending: boolean; isError: boolean; error: unknown; refetch: () => unknown } | undefined;
  canManage: boolean;
  onCreate: () => void;
  onRotate: (source: GenericSource) => void;
  onRemove: (source: GenericSource) => void;
  onMapping: (source: GenericSource) => void;
}) {
  const all = query?.data?.sources ?? [];
  const live = all.filter((s) => s.state === 'enabled');
  const removed = all.length - live.length;

  return (
    <section className="px-5 py-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-bluesphere-textOnLight">{tenant.display_name}</h3>
        {canManage && (
          <PillButton size="sm" onClick={onCreate}>
            New source
          </PillButton>
        )}
      </div>
      {!query || query.isPending ? (
        <Loading height="h-12" />
      ) : query.isError ? (
        <div className="mt-2">
          <ErrorNote error={query.error} />
        </div>
      ) : live.length === 0 ? (
        <p className="mt-1 text-sm text-bluesphere-muted">
          No sources{removed > 0 ? ` (${removed} removed)` : ''}.
        </p>
      ) : (
        <div className="mt-3 space-y-3">
          {live.map((source) => (
            <SourceRow
              key={source.source_id}
              source={source}
              canManage={canManage}
              onRotate={() => onRotate(source)}
              onRemove={() => onRemove(source)}
              onMapping={() => onMapping(source)}
            />
          ))}
          {removed > 0 && (
            <p className="text-xs text-bluesphere-muted">
              {removed} removed source{removed === 1 ? '' : 's'} not shown.
            </p>
          )}
        </div>
      )}
    </section>
  );
}

function SourceRow({
  source,
  canManage,
  onRotate,
  onRemove,
  onMapping,
}: {
  source: GenericSource;
  canManage: boolean;
  onRotate: () => void;
  onRemove: () => void;
  onMapping: () => void;
}) {
  const typed = source.source_type !== TYPE_GENERIC;
  return (
    <div className="rounded-xl border border-bluesphere-border p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-bluesphere-textOnLight">{source.name}</span>
            <SourceBadge label={typed ? sourceLabel(source.source_type) : 'Generic JSON'} />
          </div>
          <div className="mt-0.5 font-mono text-xs text-bluesphere-muted">{source.source_id}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <PillButton variant="ghost" size="sm" onClick={onMapping}>
            Mapping
          </PillButton>
          {canManage && (
            <>
              <PillButton variant="ghost" size="sm" onClick={onRotate}>
                Rotate key
              </PillButton>
              <PillButton variant="ghost" size="sm" onClick={onRemove}>
                Remove
              </PillButton>
            </>
          )}
        </div>
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
        <Stat label="Last batch" value={source.last_received_at ? timeAgo(source.last_received_at) : 'never'} title={source.last_received_at ?? undefined} />
        <Stat label="Events" value={source.events_received.toLocaleString()} />
        <Stat label="Batches" value={source.batches_received.toLocaleString()} />
        <Stat
          label="Mapping failures"
          value={source.mapping_failures.toLocaleString()}
          title={source.last_mapping_failure_at ?? undefined}
        />
        {typed && (
          <>
            <Stat label="Redactions" value={source.redactions.toLocaleString()} title={source.last_redaction_at ?? undefined} />
            <Stat label="Refusals" value={source.refusals.toLocaleString()} title={source.last_refusal_at ?? undefined} />
          </>
        )}
        <Stat label="Created" value={source.created_at ? absoluteTime(source.created_at) : '—'} />
        <Stat label="Key issued" value={source.key_issued_at ? absoluteTime(source.key_issued_at) : '—'} />
        <Stat
          label="Limits"
          value={`${source.requests_per_minute.toLocaleString()}/min · ${source.events_per_day.toLocaleString()} events/day`}
        />
      </dl>
    </div>
  );
}

function Stat({ label, value, title }: { label: string; value: string; title?: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs uppercase tracking-wider text-bluesphere-muted">{label}</dt>
      <dd className="truncate text-bluesphere-textOnLight" title={title}>
        {value}
      </dd>
    </div>
  );
}

function CreateSourceModal({
  tenant,
  canAddService,
  onCancel,
  onCreated,
}: {
  tenant: ClientTenant;
  canAddService: boolean;
  onCancel: () => void;
  onCreated: (result: GenericSourceWithKey) => void;
}) {
  const idToken = useAccessToken();
  const [name, setName] = useState('');
  const [custom, setCustom] = useState(false);
  const [mappingText, setMappingText] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const mutation = useMutation<GenericSourceWithKey, Error, { addService: boolean }>({
    mutationFn: ({ addService }) => {
      const body = custom
        ? { name: name.trim(), mapping: JSON.parse(mappingText) as Record<string, unknown> }
        : { name: name.trim(), template: TEMPLATE };
      return createGenericSource(tenant.client_tenant_id, body, idToken, { addService });
    },
    onSuccess: onCreated,
  });
  const needsService =
    mutation.error instanceof ApiError && mutation.error.code === 'service_required'
      ? mutation.error
      : null;
  const serviceName = String(
    needsService?.body.details?.display_name ?? needsService?.body.details?.service ?? 'a service',
  );

  const submit = (addService: boolean) => {
    if (custom) {
      try {
        const parsed: unknown = JSON.parse(mappingText);
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
          setParseError('The mapping must be a JSON object.');
          return;
        }
      } catch {
        setParseError('The mapping is not valid JSON.');
        return;
      }
    }
    setParseError(null);
    mutation.mutate({ addService });
  };
  const canSubmit = name.trim().length > 0 && name.trim().length <= 80 && !mutation.isPending;

  return (
    <ModalShell onClose={onCancel} width="max-w-lg">
      <div className="border-b border-bluesphere-border px-6 py-5">
        <h2 className="text-lg font-semibold text-bluesphere-textOnLight">
          New generic source for {tenant.display_name}
        </h2>
        <p className="mt-1 text-sm text-bluesphere-textSecondary">
          The source gets its own key, shown once when it is created.
        </p>
      </div>
      <form
        className="space-y-4 px-6 py-5"
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) submit(false);
        }}
      >
        <label className="block">
          <span className="text-xs uppercase tracking-wider text-bluesphere-textSecondary">Name</span>
          <input
            autoFocus
            value={name}
            maxLength={80}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Firewall syslog relay"
            className={INPUT}
          />
        </label>
        <fieldset className="space-y-2">
          <legend className="text-xs uppercase tracking-wider text-bluesphere-textSecondary">
            Field mapping
          </legend>
          <label className="flex items-start gap-2 text-sm text-bluesphere-textOnLight">
            <input type="radio" checked={!custom} onChange={() => setCustom(false)} className="mt-1" />
            <span>
              Generic JSON template: every record kept whole and unclassified. Edit it later
              under Mapping.
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm text-bluesphere-textOnLight">
            <input type="radio" checked={custom} onChange={() => setCustom(true)} className="mt-1" />
            <span>A mapping document of my own (JSON)</span>
          </label>
          {custom && (
            <textarea
              value={mappingText}
              onChange={(e) => setMappingText(e.target.value)}
              rows={8}
              spellCheck={false}
              className={`${INPUT} font-mono text-xs`}
              placeholder='{"version": 1, "class_uid": 0, "fields": {}, "keep_raw": true}'
            />
          )}
        </fieldset>
        {parseError && <ErrorNote error={new Error(parseError)} />}
        {needsService ? (
          <div className="rounded-input border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {tenant.display_name} is not engaged for {serviceName}, which covers generic sources.
            {canAddService ? (
              <div className="mt-2">
                <PillButton size="sm" type="button" onClick={() => submit(true)} disabled={mutation.isPending}>
                  Add {serviceName} and create
                </PillButton>
              </div>
            ) : (
              ' A platform Admin has to add the service before a source can be created.'
            )}
          </div>
        ) : (
          <ErrorNote error={mutation.error} />
        )}
        <div className="flex items-center justify-end gap-2 pt-2">
          <PillButton variant="ghost" size="sm" type="button" onClick={onCancel}>
            Cancel
          </PillButton>
          <PillButton size="sm" type="submit" disabled={!canSubmit}>
            {mutation.isPending ? 'Creating…' : 'Create source'}
          </PillButton>
        </div>
      </form>
    </ModalShell>
  );
}

function KeyOnceModal({
  tenant,
  result,
  onClose,
}: {
  tenant: ClientTenant;
  result: GenericSourceWithKey;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const url = `${apiBaseUrl() || 'https://<api host>'}/ingest/${result.source.source_id}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.key);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  return (
    <ModalShell onClose={onClose} width="max-w-lg">
      <div className="border-b border-bluesphere-border px-6 py-5">
        <h2 className="text-lg font-semibold text-bluesphere-textOnLight">
          Key for {result.source.name}
        </h2>
        <p className="mt-1 text-sm text-bluesphere-textSecondary">{tenant.display_name}</p>
      </div>
      <div className="space-y-4 px-6 py-5">
        <div className="rounded-input border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" role="alert">
          Copy this key now. It is not stored anywhere BlueSphere can show it again; if it is lost,
          rotate it.
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-bluesphere-textSecondary">Key</div>
          <div className="mt-1 flex items-center gap-2">
            <code className="min-w-0 flex-1 break-all rounded-input bg-bluesphere-surfaceMuted px-2 py-1.5 font-mono text-xs text-bluesphere-textOnLight">
              {result.key}
            </code>
            <PillButton size="sm" variant="ghost" onClick={() => void copy()}>
              {copied ? 'Copied' : 'Copy'}
            </PillButton>
          </div>
        </div>
        <dl className="space-y-2 text-sm">
          <HowField label="Endpoint">
            <span className="break-all font-mono text-xs">POST {url}</span>
          </HowField>
          <HowField label="Header">
            <span className="break-all font-mono text-xs">Authorization: Bearer &lt;this key&gt;</span>
          </HowField>
        </dl>
      </div>
      <div className="flex items-center justify-end border-t border-bluesphere-border px-6 py-3">
        <PillButton size="sm" onClick={onClose}>
          I have stored the key
        </PillButton>
      </div>
    </ModalShell>
  );
}

function MappingModal({
  target,
  canEdit,
  onClose,
  onSaved,
}: {
  target: Target;
  canEdit: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const idToken = useAccessToken();
  const { tenant, source } = target;
  const builtIn = source.source_type !== TYPE_GENERIC || source.mapping == null;
  const [text, setText] = useState(JSON.stringify(source.mapping ?? {}, null, 2));
  const [parseError, setParseError] = useState<string | null>(null);
  const mutation = useMutation<GenericSourceResult, Error, Record<string, unknown>>({
    mutationFn: (mapping) =>
      putGenericMapping(tenant.client_tenant_id, source.source_id, mapping, idToken),
    onSuccess: onSaved,
  });
  const editable = canEdit && !builtIn;

  const save = () => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      setParseError('The mapping is not valid JSON.');
      return;
    }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      setParseError('The mapping must be a JSON object.');
      return;
    }
    setParseError(null);
    mutation.mutate(parsed as Record<string, unknown>);
  };

  return (
    <ModalShell onClose={onClose} width="max-w-2xl">
      <div className="border-b border-bluesphere-border px-6 py-5">
        <h2 className="text-lg font-semibold text-bluesphere-textOnLight">
          Mapping for {source.name}
        </h2>
        <p className="mt-1 text-sm text-bluesphere-textSecondary">
          {builtIn
            ? 'This source’s mapping is built in and cannot be changed here.'
            : `How ${tenant.display_name}’s records become OCSF events (task 129’s grammar).${
                source.mapping_updated_at ? ` Last saved ${absoluteTime(source.mapping_updated_at)}.` : ''
              }`}
        </p>
      </div>
      <div className="space-y-3 px-6 py-5">
        {builtIn ? (
          <p className="text-sm text-bluesphere-textOnLight">
            {sourceLabel(source.source_type)} events are normalized by BlueSphere&rsquo;s own mapping
            for that source.
          </p>
        ) : (
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            readOnly={!editable}
            rows={16}
            spellCheck={false}
            className={`${INPUT} font-mono text-xs`}
          />
        )}
        {parseError && <ErrorNote error={new Error(parseError)} />}
        <ErrorNote error={mutation.error} />
      </div>
      <div className="flex items-center justify-end gap-2 border-t border-bluesphere-border px-6 py-3">
        <PillButton variant="ghost" size="sm" onClick={onClose}>
          {editable ? 'Cancel' : 'Close'}
        </PillButton>
        {editable && (
          <PillButton size="sm" onClick={save} disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Save mapping'}
          </PillButton>
        )}
      </div>
    </ModalShell>
  );
}
